import { useEffect, useMemo, useState } from 'react';

import { Collapse, Grid, Typography, useMediaQuery } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import {
  calculatorAppreciationToEquity,
  MEDIA_QUERIES,
  shouldShowAirDnaHistoricalCharts,
  useStrOfferingData,
} from '@arrived/common';
import { FEATURE_FLAGS, useIsFeatureFlagEnabled } from '@arrived/feature-flags';
import { AssetType, MarketROI, Offering } from '@arrived/models';
import { useOfferingDividendsQuery } from '@arrived/queries';

import { ZillowHomePriceByZipCodeGraph } from '../../../charts/ZillowHomePriceByZipCodeGraph';
import { AppreciationGrowthCard } from './AppreciationGrowthCard';
import { DividendChart } from './DividendChart';
import { RentalCard } from './RentalCard';
import { CalculatorEstimatedReturn } from './ROICalculatorPage/CalculatorEstimatedReturn';
import { STRHistoricalInfoSection } from './STRHistoricalInfoSection';
import { TotalReturnSection } from './TotalReturnSection';

import { useMediaQueryClasses } from '../../../hooks/useMediaQueryClasses';

const StyledCalculatorContainer = styled(Grid)`
  &.calculator-container {
    margin-top: ${({ theme }) => theme.spacing(2)};

    .calculator-inner-title {
      display: flex;
      width: 100%;
      gap: ${({ theme }) => theme.spacing(1)};
      flex-direction: column;
      margin-top: ${({ theme }) => theme.spacing(2)};
      min-height: 60px;
      align-items: center;
      text-align: center;
    }

    .rental-income-card {
      &:not(.large-screen):not(.extra-large-screen) {
        margin-top: ${({ theme }) => theme.spacing(3)};
      }
    }

    .calculator-property-appreciation-title {
      color: ${({ theme }) => theme.palette.darkened.steel};
    }

    .title-divider {
      width: 50%;
      border-bottom: 2px solid ${({ theme }) => theme.palette.darkened.steel};
    }

    .calculator-view-charts-button {
      margin: ${({ theme }) => theme.spacing(2, 0)};
    }

    .chart-container {
      transition: padding 0.2s ease-in-out, height 0.2s ease-in-out;

      .chart-title {
        display: flex;
        justify-content: center;
      }

      &.hide {
        padding-top: 0;
      }
    }
  }
`;

export interface ROISectionProps {
  className?: string;
  debtLeverage: number;
  marketData: MarketROI;
  zipCode?: string;
  offering: Offering;
}

export const ROISection = ({ offering, ...rest }: ROISectionProps) => {
  const { projectedAnnualDividendYield, projectedAppreciationLower, projectedAppreciationUpper } = offering;
  const offeringDividendsState = useOfferingDividendsQuery(offering.id);

  const initialDividendRate = useMemo(
    () =>
      offeringDividendsState.data?.length
        ? offeringDividendsState.data[0].annualizedDividendPercent / 100
        : projectedAnnualDividendYield,
    [offeringDividendsState.data, projectedAnnualDividendYield]
  );

  const initialAppreciationRate = useMemo(
    () =>
      projectedAppreciationLower && projectedAppreciationUpper
        ? (projectedAppreciationLower + projectedAppreciationUpper) / 2
        : 0,
    [projectedAppreciationLower, projectedAppreciationUpper]
  );

  if (!projectedAppreciationUpper || !projectedAppreciationLower || initialDividendRate == null) {
    return null;
  }

  return (
    <ROISectionContent
      initialAppreciationRate={initialAppreciationRate}
      initialDividendRate={initialDividendRate}
      offering={offering}
      projectedAppreciationLower={projectedAppreciationLower}
      projectedAppreciationUpper={projectedAppreciationUpper}
      {...rest}
    />
  );
};

interface ROISectionContentProps extends ROISectionProps {
  initialAppreciationRate: number;
  initialDividendRate: number;
  projectedAppreciationLower: number;
  projectedAppreciationUpper: number;
}

const ROISectionContent = ({
  className,
  debtLeverage,
  initialAppreciationRate,
  initialDividendRate,
  marketData,
  offering,
  projectedAppreciationLower,
  projectedAppreciationUpper,
  zipCode,
}: ROISectionContentProps) => {
  const { assetType } = offering;
  const mediaQueryClasses = useMediaQueryClasses();
  const isLargeScreen = useMediaQuery(MEDIA_QUERIES.isLargeScreen);
  const isExtraLargeScreen = useMediaQuery(MEDIA_QUERIES.isExtraLargeScreen);
  const isFullLayout = isLargeScreen || isExtraLargeScreen;
  const [showCharts, setShowCharts] = useState(false);
  const isLTR = assetType === AssetType.LTR;
  const airDnaHistoricalChartsEnabled = useIsFeatureFlagEnabled(FEATURE_FLAGS.AIR_DNA_HISTORICAL_CHARTS);
  const showAirDnaHistoricalCharts = useMemo(
    () => shouldShowAirDnaHistoricalCharts(offering.status),
    [offering.status]
  );

  const { marketName, averageDividend, quarterlyDividends } = marketData;

  const [dividendRate, setDividendRate] = useState(initialDividendRate);
  const [appreciationRate, setAppreciationRate] = useState(initialAppreciationRate);

  useEffect(() => {
    setDividendRate(initialDividendRate);
  }, [initialDividendRate]);

  useEffect(() => {
    setAppreciationRate(initialAppreciationRate);
  }, [initialAppreciationRate]);

  const equityGrowthRate = useMemo(
    () => calculatorAppreciationToEquity(appreciationRate, offering),
    [appreciationRate, offering]
  );

  const appreciationChart = useMemo(
    () => (
      <Grid item xs={12} lg={6} className={clsx('chart-container', { hide: !showCharts })}>
        <Collapse in={showCharts}>
          <Typography className="chart-title" variant="body1">
            {`${marketName} (${zipCode})`}
          </Typography>
          <ZillowHomePriceByZipCodeGraph zipCode={zipCode ?? ''} />
        </Collapse>
      </Grid>
    ),
    [showCharts, marketName, zipCode]
  );

  const rentChart = useMemo(
    () => (
      <Grid item xs={12} lg={6} className={clsx('chart-container', { hide: !showCharts })}>
        <Collapse in={showCharts}>
          <DividendChart marketTitle={marketName} quarterlyDividends={quarterlyDividends} />
        </Collapse>
      </Grid>
    ),
    [showCharts, marketName, quarterlyDividends]
  );

  const toggleShowCharts = () => setShowCharts(() => !showCharts);

  const { historicalData } = useStrOfferingData(offering.id);

  return (
    <StyledCalculatorContainer
      className={clsx('calculator-container', className)}
      rowSpacing={4}
      columnSpacing={2}
      container
    >
      <Grid item xs={12} lg={6}>
        <AppreciationGrowthCard
          appreciation={{
            lower: projectedAppreciationLower,
            upper: projectedAppreciationUpper,
          }}
          appreciationRate={appreciationRate}
          setAppreciationRate={setAppreciationRate}
          equityGrowthRate={equityGrowthRate}
          debtLeverage={debtLeverage}
          showCharts={showCharts}
          marketName={marketName}
          toggleShowCharts={toggleShowCharts}
        />
      </Grid>
      {!isFullLayout && appreciationChart}
      <Grid item xs={12} lg={6} className={clsx('rental-income-card', mediaQueryClasses)}>
        <RentalCard
          currentDividendRate={initialDividendRate}
          averageDividendRate={averageDividend}
          dividendRate={dividendRate}
          setDividendRate={setDividendRate}
          showCharts={showCharts}
          marketName={marketName}
          toggleShowCharts={toggleShowCharts}
          isLTR={isLTR}
        />
      </Grid>
      {!isFullLayout && isLTR && rentChart}
      {isFullLayout && appreciationChart}
      {isFullLayout && isLTR && rentChart}
      <Grid item xs={12}>
        <TotalReturnSection
          totalReturnRate={dividendRate + equityGrowthRate}
          isDirty={dividendRate !== initialDividendRate || appreciationRate !== initialAppreciationRate}
        />
      </Grid>

      <Grid item xs={12}>
        <CalculatorEstimatedReturn equityPercent={equityGrowthRate} rentDividendPercent={dividendRate} />
      </Grid>

      {airDnaHistoricalChartsEnabled &&
        showAirDnaHistoricalCharts &&
        offering.assetType === AssetType.STR &&
        historicalData && (
          <Grid item xs={12}>
            <STRHistoricalInfoSection historicalData={historicalData} />
          </Grid>
        )}
    </StyledCalculatorContainer>
  );
};
